/**
 * Environment Configuration
 *
 * WHY: Centralize environment variable access with validation
 * WHAT: Validates and exports typed environment variables
 * BENEFIT:
 *   - Catch missing env vars at startup, not at runtime
 *   - Type-safe access throughout the app
 *   - Easy to mock in tests
 */

/**
 * Validates that a required environment variable exists
 * @throws Error if the variable is not defined
 */

const getRequiredEnv = (
  key: keyof ImportMetaEnv,
  fallback?: string,
): string => {
  const value = import.meta.env[key] || fallback;

  if (!value) {
    throw new Error(
      `❌ Missing required environment variable: ${key}\n` +
        `Please check your .env file and ensure ${key} is defined.`,
    );
  }
  return value;
};

/**
 * Gets an optional environment variable with fallback
 */
const getOptionalEnv = (key: keyof ImportMetaEnv, fallback: string): string => {
  return import.meta.env[key] || fallback;
};

/**
 * Converts string to boolean
 * WHY: Environment variables are always strings
 */

const toBool = (value: string): boolean => {
  return value === "true" || value === "1";
};

/**
 * Converts string to number
 */

const toNumber = (value: string, fallback: number): number => {
  const num = parseInt(value, 10);
  return isNaN(num) ? fallback : num;
};

// =======================
// 🔹 Environment Configuration Object
// =======================

export const envConfig = {
  // Application Info
  app: {
    name: getOptionalEnv("VITE_APP_NAME", "School Of Bitcoin"),
    version: getOptionalEnv("VITE_APP_VERSION", "1.0.0"),
    env: getOptionalEnv("VITE_APP_ENV", "development") as
      | "development"
      | "production",
  },
  // API Configurations
  api: {
    baseURL: getRequiredEnv(
      "VITE_API_BASE_URL",
      "http://localhost:8000/api/v1",
    ),
    timeout: toNumber(getOptionalEnv("VITE_API_TIMEOUT", "30000"), 30000),
  },

  /**
   * Error Monitoring Configuration (Sentry)
   */
  sentry: {
    /** Sentry DSN - required for error reporting */
    dsn: import.meta.env.VITE_SENTRY_DSN || "",
    /** Current release version for tracking */
    release: import.meta.env.VITE_APP_VERSION || "1.0.0",
    /** Environment name for Sentry */
    environment: import.meta.env.VITE_SENTRY_ENV || import.meta.env.MODE,
  },

  // Feature Flags
  features: {
    enableLogger: toBool(getOptionalEnv("VITE_ENABLE_LOGGER", "true")),
    /** Enable error reporting to external service */
    enableErrorReporting: toBool(
      getRequiredEnv("VITE_ENABLE_ERROR_REPORTING", "true"),
    ),
    /** Enable performance monitoring */
    enablePerformanceMonitoring: toBool(
      getRequiredEnv("VITE_ENABLE_PERF_MONITORING", "true"),
    ),
  },

  isDevelopment:
    getOptionalEnv("VITE_APP_ENV", "development") === "development",
  isProduction: getOptionalEnv("VITE_APP_ENV", "development") === "production",
} as const;

// Type-safe export
export type envConfig = typeof envConfig;

if (envConfig.isDevelopment) {
  console.log("🔧 Environment Configuration:", envConfig);
}
